import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

import { api, queries, unwrap, type AirframeSummary, type Me } from "@/api";
import { Welcome } from "@/components/Welcome";
import { useHost } from "@/host/host";
import { hostQueries } from "@/host/queries";
import type { StatusRow } from "@/host/types";
import { ShareAnAirframe } from "@/components/ShareAirframe";
import { ErrorNote } from "@/components/ErrorNote";
import { FleetMap } from "@/components/FleetMap";
import { LiveNow, YourAircraftNow, useLives } from "@/components/LiveNow";
import { Landing } from "@/components/Landing";
import { openNotifications } from "@/components/NotificationsDrawer";
import { FleetTable } from "@/components/FleetTable";
import { GetTheApp } from "@/components/GetTheApp";
import { Tile } from "@/components/Tile";
import { formatDateTime, formatHours, formatRelative } from "@/lib/format";

export const Route = createFileRoute("/")({
  component: Home,
});

function Home() {
  const me = useQuery(queries.me());
  const host = useHost();
  if (me.isPending) return null;
  if (me.data) return <FleetDesk me={me.data} />;
  return host ? <Welcome /> : <Landing />;
}

/** The fleet manager's desk (F413, decision 055): the numbers, the fleet, what happened, what waits. */
function FleetDesk({ me }: { me: Me }) {
  // Every ten seconds (decision 071, F442): a flight moves on the map and "Booked, flying" follows the holder's publication.
  const hangar = useQuery({ ...queries.airframes("member", ""), refetchInterval: 10_000 });
  const feed = useQuery(queries.notifications());
  const transfers = useQuery(queries.transfers());
  // The desk in two tabs (decision 071, F439): your hangar (the airframes
  // you own, on the platform or still only on this PC) and the airframes
  // of others you fly; the hangar sorted and filtered.
  const host = useHost();
  const [tab, setTab] = useState<"hangar" | "others">("hangar");
  const [filter, setFilter] = useState<HangarFilter>("all");
  const [sort, setSort] = useState<HangarSort>("flown");
  const status = useQuery({ ...hostQueries.status(host!), enabled: !!host });
  const allItems = hangar.data?.items ?? [];
  const items = allItems;
  // An airframe the owner took off ShariFly (decision 138) stays out of the hangar unless asked for.
  const owned = allItems.filter((a) => a.your_role === "owner" && !a.unlisted_at);
  const unlistedRows = allItems.filter((a) => a.your_role === "owner" && !!a.unlisted_at);
  const others = allItems.filter((a) => a.your_role !== "owner");
  const toShare: StatusRow[] = host ? (status.data ?? []).filter((r) => r.classification === "personal" && r.platform === null) : [];
  // The platform unreachable (decision 139): the aircraft of this PC stay in sight, with the reason.
  const offline: StatusRow[] = host ? (status.data ?? []).filter((r) => r.classification === "offline") : [];
  const hangarRows = sortAirframes(filter === "flown" ? owned.filter((a) => a.last_landing) : filter === "to_share" ? [] : filter === "unlisted" ? unlistedRows : owned, sort);
  const showToShare = filter === "all" || filter === "to_share";
  // The list is paged (100 rows): the count is the list's total, the sums
  // are over the rows shown and say so past the first page.
  const total = hangar.data?.total ?? items.length;
  const partial = total > items.length;
  const checkedOut = items.filter((a) => a.lease).length;
  const flights = items.reduce((n, a) => n + a.cycles, 0);
  const hours = items.reduce((n, a) => n + a.total_hours, 0);
  const mine = items.filter((a) => a.lease?.holder.id === me.user.id);
  // The live conditions of the flights going on (F471): the strip, your aircraft and the map read the same answers.
  const lives = useLives(allItems);
  const open = transfers.data ?? [];
  const unread = feed.data?.unread ?? 0;
  return (
    <div className="space-y-6">
      <header className="page-head">
        <div>
          <h1 className="page-title">Fleet</h1>
          <p className="muted text-sm">
            {total === 0 ? "No airframe to fly yet." : `${total} airframe${total === 1 ? "" : "s"} you fly, ${owned.length} of them yours, ${checkedOut} booked now${partial ? ` among the first ${items.length}` : ""}.`}
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link to="/airframes" search={{ scope: "public", q: "", joined: "exclude", continent: "", sort: "updated" }} className="btn">
            Marketplace
          </Link>
          <Link to="/guide" className="btn" data-testid="guide-link">
            Help
          </Link>
        </div>
      </header>
      <ErrorNote error={hangar.error} />
      {offline.length > 0 ? (
        <section className="card space-y-2" aria-labelledby="offline-title" data-testid="offline-rows">
          <h2 id="offline-title" className="text-[1.0714rem] font-semibold">
            ShariFly cannot be reached right now
          </h2>
          <p className="muted text-sm">These aircraft are on this PC as they were. Nothing is booked or returned until ShariFly answers again; the app keeps trying.</p>
          <ul className="divide-y divide-line">
            {offline.map((r) => (
              <li key={r.uuid} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="font-semibold tracking-tight">{r.reg}</span>
                  <span className="muted block text-xs">
                    MSN <span className="tnum">{r.msn}</span> · {r.livery || "Default livery"} · {r.flights} flights in the log{r.error ? ` · ${r.error}` : ""}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
      {/* In a browser the app may not be on the PC yet (decision 137): the download, until the pilot says it is. */}
      {host ? null : <GetTheApp dismissable />}
      <LiveNow items={allItems} lives={lives} meId={me.user.id} />
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="fleet-tiles">
        <Tile label="Airframes you fly" value={total} note={`${items.filter((a) => a.your_role === "owner").length} owned by you${partial ? ` among the first ${items.length}` : ""}`} testId="tile-hangar" />
        <Tile label="Booked now" value={checkedOut} note={mine.length > 0 ? `${mine.map((a) => a.reg).join(", ")} by you` : "none by you"} tone={checkedOut > 0 ? "warn" : undefined} testId="tile-checked-out" />
        <Tile label="Flights" value={flights} note={partial ? `across the first ${items.length}` : "across the hangar"} testId="tile-flights" />
        <Tile label="Hours" value={formatHours(hours)} note={partial ? `logged by the first ${items.length}` : "enroute time logged"} testId="tile-hours" />
      </div>
      {hangar.data ? <FleetMap items={allItems} meId={me.user.id} /> : null}
      <YourAircraftNow items={allItems} lives={lives} meId={me.user.id} />
      <section className="card-flush">
        <div className="card-head flex-wrap">
          <div className="segs" role="tablist" aria-label="Your airframes">
            <button type="button" role="tab" aria-selected={tab === "hangar"} className={`seg ${tab === "hangar" ? "seg-active" : ""}`} onClick={() => setTab("hangar")} data-testid="tab-hangar">
              Your hangar ({owned.length + toShare.length})
            </button>
            <button type="button" role="tab" aria-selected={tab === "others"} className={`seg ${tab === "others" ? "seg-active" : ""}`} onClick={() => setTab("others")} data-testid="tab-others">
              Shared with you ({others.length})
            </button>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {tab === "hangar" ? (
              <select className="input" value={filter} onChange={(e) => setFilter(e.target.value as HangarFilter)} aria-label="Show" data-testid="hangar-filter">
                <option value="all">All of yours</option>
                {host ? <option value="shared">On ShariFly</option> : null}
                {host ? <option value="to_share">Only on this PC</option> : null}
                <option value="flown">Flown at least once</option>
                {unlistedRows.length > 0 ? <option value="unlisted">Unlisted ({unlistedRows.length})</option> : null}
              </select>
            ) : null}
            <select className="input" value={sort} onChange={(e) => setSort(e.target.value as HangarSort)} aria-label="Sort by" data-testid="hangar-sort">
              <option value="flown">Latest flown first</option>
              <option value="reg">Registration</option>
              <option value="hours">Most hours first</option>
            </select>
          </div>
          <ShareAnAirframe />
        </div>
        {hangar.data ? (
          tab === "hangar" ? (
            <>
              {hangarRows.length === 0 && (!showToShare || toShare.length === 0) ? (
                <p className="muted p-5 text-sm" data-testid="hangar-empty">
                  {filter !== "all" ? "Nothing matches." : host ? "Nothing in your hangar yet. Share an airframe of this PC, or ask a friend for a link." : "Nothing of your own yet. Share an airframe from the app on your PC, or ask a friend for a link to theirs."}
                </p>
              ) : null}
              {hangarRows.length > 0 ? <FleetTable items={hangarRows} /> : null}
              {showToShare && toShare.length > 0 ? (
                <ul className="divide-y divide-line" data-testid="to-share" aria-label="airframes of this PC not on ShariFly yet">
                  {toShare.map((r) => (
                    <li key={r.uuid} className="flex flex-wrap items-center gap-3 px-5 py-2.5 text-sm">
                      <span className="min-w-0 flex-1">
                        <span className="font-semibold tracking-tight">{r.reg}</span>
                        <span className="muted block text-xs">
                          MSN <span className="tnum">{r.msn}</span> · {r.livery || "Default livery"} · {r.flights} flights in the log · on this PC, not on ShariFly yet
                        </span>
                      </span>
                      <ShareAnAirframe row={r} />
                    </li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : others.length === 0 ? (
            <p className="muted p-5 text-sm" data-testid="others-empty">
              No airframe of someone else yet. The Marketplace has the ones you can join.
            </p>
          ) : (
            <FleetTable items={sortAirframes(others, sort)} />
          )
        ) : null}
      </section>
      <div className="grid gap-4 lg:grid-cols-3">
        <section className="card-flush lg:col-span-2">
          <div className="card-head">
            <h2 className="text-[1.0714rem] font-semibold">Activity</h2>
            <span className="muted ml-auto text-xs">the last of what happened on your airframes</span>
          </div>
          <ul className="divide-y divide-line" data-testid="activity">
            {(feed.data?.items ?? []).slice(0, 12).map((n) => (
              <li key={n.id} className={`flex items-start gap-3 px-5 py-2.5 text-sm ${n.read_at ? "opacity-70" : ""}`}>
                <span className="muted w-28 shrink-0 text-xs tnum">{formatRelative(n.created_at)}</span>
                <span className="min-w-0 flex-1">
                  <span className="font-medium">{n.title}</span>
                  <span className="muted block truncate text-xs">{n.body}</span>
                </span>
                {n.link ? (
                  <Link to={n.link} className="muted text-xs underline">
                    Open
                  </Link>
                ) : null}
              </li>
            ))}
            {feed.data && feed.data.items.length === 0 ? <li className="muted px-5 py-3 text-sm">Nothing yet. Requests, bookings and returns on your airframes land here.</li> : null}
          </ul>
        </section>
        <section className="card-flush">
          <div className="card-head">
            <h2 className="text-[1.0714rem] font-semibold">To do</h2>
          </div>
          <ul className="divide-y divide-line text-sm" data-testid="todo">
            <PendingRequests airframes={allItems} />
            <ReturnsWaiting />
            {open.map((t) => (
              <li key={t.id} className="flex items-center gap-3 px-5 py-2.5">
                <span className="min-w-0 flex-1">
                  {t.to.id === me.user.id ? `Take over ${t.reg} as owner` : `${t.reg} offered to ${t.to.name}`}
                  <span className="muted block text-xs">expires {formatDateTime(t.expires_at)}</span>
                </span>
                <Link to="/transfers" className="btn btn-sm">
                  Open
                </Link>
              </li>
            ))}
            {unread > 0 ? (
              <li className="flex items-center gap-3 px-5 py-2.5">
                <span className="flex-1">
                  {unread} unread notification{unread === 1 ? "" : "s"}
                </span>
                <button type="button" className="btn btn-sm" onClick={openNotifications} data-testid="todo-notifications">
                  Read
                </button>
              </li>
            ) : null}
            {open.length === 0 && unread === 0 ? <li className="muted px-5 py-3">Nothing waits for you.</li> : null}
          </ul>
        </section>
      </div>
    </div>
  );
}

/** The requests to join the airframes you manage (decision 065): approved or declined from the desk. */
function PendingRequests({ airframes }: { airframes: AirframeSummary[] }) {
  const queryClient = useQueryClient();
  const managed = airframes.filter((a) => a.your_role === "owner" || a.your_role === "captain");
  const pending = useQueries({ queries: managed.map((a) => queries.members(a.id, "pending")) });
  const act = useMutation({
    mutationFn: async ({ id, uid, action }: { id: string; uid: string; action: "approve" | "reject" }) =>
      unwrap(await api.POST("/api/v1/airframes/{id}/members/{target}", { params: { path: { id, target: `${uid}:${action}` } } })),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ["members", id] });
      queryClient.invalidateQueries({ queryKey: ["airframe", id] });
      queryClient.invalidateQueries({ queryKey: ["airframes"] });
    },
  });
  const rows = managed.flatMap((a, i) => (pending[i]?.data ?? []).map((m) => ({ airframe: a, member: m })));
  return (
    <>
      {rows.map(({ airframe, member }) => (
        <li key={`${airframe.id}-${member.id}`} className="flex items-center gap-3 px-5 py-2.5" data-testid="todo-request">
          <span className="min-w-0 flex-1">
            {member.name} asks to join {airframe.reg}
            {member.message ? <span className="muted block truncate text-xs">{member.message}</span> : null}
          </span>
          <button type="button" className="btn btn-sm btn-primary" disabled={act.isPending} onClick={() => act.mutate({ id: airframe.id, uid: member.id, action: "approve" })}>
            Approve
          </button>
          <button type="button" className="btn btn-sm" disabled={act.isPending} onClick={() => act.mutate({ id: airframe.id, uid: member.id, action: "reject" })}>
            Decline
          </button>
        </li>
      ))}
    </>
  );
}

/** The returns of this PC waiting to be sent (decision 065): a line on the desk under the host. */
function ReturnsWaiting() {
  const host = useHost();
  const outbox = useQuery({ ...hostQueries.outbox(host!), enabled: !!host });
  const waiting = outbox.data?.pending.length ?? 0;
  if (!host || waiting === 0) return null;
  return (
    <li className="flex items-center gap-3 px-5 py-2.5" data-testid="todo-returns">
      <span className="min-w-0 flex-1">
        {waiting} return{waiting === 1 ? "" : "s"} waiting to be sent
        <span className="muted block text-xs">The next return sends them; This PC shows them.</span>
      </span>
      <Link to="/pc/returns" className="btn btn-sm">
        Open
      </Link>
    </li>
  );
}

type HangarFilter = "all" | "shared" | "to_share" | "flown" | "unlisted";
type HangarSort = "flown" | "reg" | "hours";

/** The hangar's order: the latest flown first (the never flown last), the registration, or the hours. */
export function sortAirframes(rows: AirframeSummary[], sort: HangarSort): AirframeSummary[] {
  const flown = (a: AirframeSummary) => (a.last_landing ? new Date(a.last_landing.utc).getTime() : 0);
  return [...rows].sort((a, b) => {
    if (sort === "reg") return a.reg.localeCompare(b.reg);
    if (sort === "hours") return b.total_hours - a.total_hours || a.reg.localeCompare(b.reg);
    return flown(b) - flown(a) || a.reg.localeCompare(b.reg);
  });
}
